import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import {
  WebGLRenderer,
  PerspectiveCamera,
  Scene,
  Group,
  Box3,
  Vector3,
  Matrix4,
  MathUtils,
  TextureLoader,
  SpriteMaterial,
  CanvasTexture,
  Sprite,
  PlaneGeometry,
  MeshBasicMaterial,
  Mesh,
  SRGBColorSpace,
} from "three";

import { setupTracking } from "./libs/track-pan";

import { Text } from "troika-three-text";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";

import { fitCameraToObject } from "./camera/camera-operations";
//import { createWindLayer } from "./layers/data-specific/flow-pixels/layer";

import { buildField } from "./../layers/field/pitch-field";
import gsap from "gsap";

class Pitch {
  constructor(canvasReference, viewportSize) {
    this.canvasReference = canvasReference;
    this.viewportSize = viewportSize;

    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.controls = null;
    this.container = new Group();
    this.playing = false;
    this.active = true;
    this.ready = false;
    this.frameIndex = 0;
    this.playerContainers = [];

    this.layerStack = [];
    this.fieldDomain = { u: 108, v: 68 };

    this.mode = "dark";

    this.init();
  }

  init() {
    const { width, height } = this.viewportSize;
    const aspect = width / height;

    this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: false,
      canvas: this.canvasReference,
      logarithmicDepthBuffer: false,
    });

    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.camera = new PerspectiveCamera(45, aspect, 0.001, 1000);
    this.camera.position.z = 1.5;

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.scene = new Scene();
    this.scene.add(this.container);

    this.playerGroup = new Group();
    this.playerGroup.renderOrder = 1000;
    this.scene.add(this.playerGroup);

    this.renderer.setClearColor(this.clearColor, 1);

    this.controls.addEventListener("change", () => {
      this.updateField(this.camera);
      this.drawFrame();
    });

    this.controls.enabled = false;

    window.addEventListener("resize", this.resize.bind(this));
    this.resize();

    /*
    const renderPass = new RenderPass(this.scene, this.camera);

    const bloomPass = new UnrealBloomPass(new Vector3(width, height), 1.5, 0.4, 0.85);
    bloomPass.threshold = 0;
    bloomPass.strength = 1.5;
    bloomPass.radius = 0; 

    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(renderPass);
    this.composer.addPass(bloomPass);
    */

    const { container: field, updateField, updateFieldColors } = buildField();
    this.updateField = updateField;
    this.updateFieldColors = updateFieldColors;
    this.container.add(field);
    this.toggleTheme(this.mode);
  }

  toggleTheme(forceMode) {
    const setColors = (mode) => {
      switch (mode) {
        case "dark":
          this.clearColor = 0x141414;

          this.updateFieldColors(0x7c8084);
          break;
        case "light":
          this.clearColor = 0xf2f5ee;
          this.updateFieldColors(0xb6b9b4); // 0x7c7d79

          break;
      }

      this.renderer.setClearColor(this.clearColor);
    };

    if (forceMode) {
      setColors(forceMode);
    } else {
      this.mode = this.mode == "dark" ? "light" : "dark";
      setColors(this.mode);
    }
  }

  drawFrame() {
    if (this.ready) this.windFrame();

    this.layerStack.forEach((layer) => {
      layer.drawFrame();
    });

    //if(this.sim) this.sim.updateSpheres();

    if (this.sim && this.sim.polylineStripMesh) {
      this.sim.polylineStripMesh.material.uniforms.u_Time.value =
        performance.now() / 100.0;
    }

    this.playerContainers?.forEach((container) => {
      container.rotation.setFromRotationMatrix(this.camera.matrix);
    });

    this.renderer.render(this.scene, this.camera);
    //if(this.composer)this.composer.render();
  }

  resize() {
    const { width, height } = this.viewportSize;
    const aspect = width / height;

    this.renderer.setSize(width, height, true);
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.drawFrame();
  }

  animate() {
    if (this.playing) {
      this.drawFrame();
    }
    if (this.active) requestAnimationFrame(this.animate.bind(this));
  }

  startAnimation(cb) {
    if (this.playing) return;
    this.playing = true;
    this.cb = cb;
    this.animate();
  }

  dispose() {
    this.active = false;
    this.scene.traverse((object) => {
      if (!object.isMesh) return;
      object.geometry.dispose();
      if (object.material.isMaterial) {
        this.cleanMaterial(object.material);
      } else {
        for (const material of object.material) this.cleanMaterial(material);
      }
    });
    this.renderer.dispose();
    this.scene.clear();
    this.container.clear();
  }

  cleanMaterial(material) {
    material.dispose();
    for (const key of Object.keys(material)) {
      const value = material[key];
      if (value && typeof value === "object" && "minFilter" in value) {
        value.dispose();
      }
    }
  }

  fitView(duration = 1.4) {
    const boundingBox = new Box3().setFromObject(this.container);
    const center = boundingBox.getCenter(new Vector3());
    const size = boundingBox.getSize(new Vector3());
    const maxDim = Math.max(size.x, size.y);
    const fov = this.camera.fov * (Math.PI / 180);
    const aspect = this.camera.aspect;
    let distance = maxDim / (2 * Math.tan(fov / 2));
    if (aspect > 1) distance /= aspect;
    const cameraZ = distance * 1;
    const targetPosition = new Vector3(center.x, center.y, cameraZ);
    const targetLookAt = new Vector3(center.x, center.y, 0);
    const currentLookAt = new Vector3();
    this.camera.getWorldDirection(currentLookAt);
    currentLookAt.add(this.camera.position);
    const lookAtObject = {
      x: currentLookAt.x,
      y: currentLookAt.y,
      z: currentLookAt.z,
    };

    gsap.to(this.camera.position, {
      x: targetPosition.x,
      y: targetPosition.y,
      z: targetPosition.z,
      duration: duration,
      ease: "elastic.out(0.7, 0.3)",
      onUpdate: () => {
        this.camera.lookAt(lookAtObject.x, lookAtObject.y, lookAtObject.z);
      },
      onComplete: () => {
        const minZ = boundingBox.min.z;
        const maxZ = boundingBox.max.z;
        this.camera.near = Math.max(0.0001, minZ - cameraZ);
        this.camera.far = Math.max(1000, maxZ + cameraZ);
        this.camera.updateProjectionMatrix();
        this.controls.target.copy(center);
        this.controls.update();
      },
    });

    gsap.to(lookAtObject, {
      x: targetLookAt.x,
      y: targetLookAt.y,
      z: targetLookAt.z,
      duration: duration,
      ease: "elastic.out(0.7, 0.3)",
      onUpdate: () => {
        this.camera.lookAt(lookAtObject.x, lookAtObject.y, lookAtObject.z);
        this.drawFrame();
      },
    });
  }

  fitView(duration = 1.4) {
    const boundingBox = new Box3().setFromObject(this.container);
    const center = boundingBox.getCenter(new Vector3());
    const size = boundingBox.getSize(new Vector3());
    const maxDim = Math.max(size.x, size.y);
    const fov = this.camera.fov * (Math.PI / 180);
    const aspect = this.camera.aspect;
    let distance = maxDim / (2 * Math.tan(fov / 2));
    if (aspect > 1) distance /= aspect;
    const cameraZ = distance * 1;
    const targetPosition = new Vector3(center.x, center.y, cameraZ);
    const targetLookAt = new Vector3(center.x, center.y, 0);
    const currentLookAt = new Vector3();
    this.camera.getWorldDirection(currentLookAt);
    currentLookAt.add(this.camera.position);
    const lookAtObject = {
      x: currentLookAt.x,
      y: currentLookAt.y,
      z: currentLookAt.z,
    };

    gsap.to(this.camera.position, {
      x: targetPosition.x,
      y: targetPosition.y,
      z: targetPosition.z,
      duration: duration,
      ease: "elastic.out(0.7, 0.3)",
      onUpdate: () => {
        this.camera.lookAt(lookAtObject.x, lookAtObject.y, lookAtObject.z);
      },
      onComplete: () => {
        const minZ = boundingBox.min.z;
        const maxZ = boundingBox.max.z;
        this.camera.near = Math.max(0.0001, minZ - cameraZ);
        this.camera.far = Math.max(1000, maxZ + cameraZ);
        this.camera.updateProjectionMatrix();
        this.controls.target.copy(center);
        this.controls.update();
      },
    });

    gsap.to(lookAtObject, {
      x: targetLookAt.x,
      y: targetLookAt.y,
      z: targetLookAt.z,
      duration: duration,
      ease: "elastic.out(0.7, 0.3)",
      onUpdate: () => {
        this.camera.lookAt(lookAtObject.x, lookAtObject.y, lookAtObject.z);
        this.drawFrame();
      },
    });
  }

  angleView(duration = 1.4) {
    const boundingBox = new Box3().setFromObject(this.container);
    const center = boundingBox.getCenter(new Vector3());
    const size = boundingBox.getSize(new Vector3());
    const maxDim = Math.max(size.x, size.y);
    const fov = this.camera.fov * (Math.PI / 180);
    const aspect = this.camera.aspect;
    let distance = maxDim / (2 * Math.tan(fov / 2));
    if (aspect > 1) distance /= aspect;

    // Calculate the distance and position for a 45-degree angle view
    const cameraZ = distance * 1.1; // Increase distance for better view
    const angle = -Math.PI / 4; // 45 degrees in radians
    const targetPosition = new Vector3(
      center.x,
      center.y + Math.sin(angle) * cameraZ,
      cameraZ
    );
    const targetLookAt = new Vector3(center.x, center.y, 0);

    const currentLookAt = new Vector3();
    this.camera.getWorldDirection(currentLookAt);
    currentLookAt.add(this.camera.position);
    const lookAtObject = {
      x: currentLookAt.x,
      y: currentLookAt.y,
      z: currentLookAt.z,
    };

    gsap.to(this.camera.position, {
      x: targetPosition.x,
      y: targetPosition.y,
      z: targetPosition.z,
      duration: duration,
      ease: "elastic.out(0.7, 0.3)",
      onUpdate: () => {
        this.camera.lookAt(lookAtObject.x, lookAtObject.y, lookAtObject.z);
      },
      onComplete: () => {
        const minZ = boundingBox.min.z;
        const maxZ = boundingBox.max.z;
        this.camera.near = Math.max(0.0001, minZ - cameraZ);
        this.camera.far = Math.max(1000, maxZ + cameraZ);
        this.camera.updateProjectionMatrix();
        this.controls.target.copy(center);
        this.controls.update();
      },
    });

    gsap.to(lookAtObject, {
      x: targetLookAt.x,
      y: targetLookAt.y,
      z: targetLookAt.z,
      duration: duration,
      ease: "elastic.out(0.7, 0.3)",
      onUpdate: () => {
        this.camera.lookAt(lookAtObject.x, lookAtObject.y, lookAtObject.z);
        this.drawFrame();
      },
    });
  }

  smoothCameraOverMatrix() {
    const startMatrix = new Matrix4().copy(this.camera.matrix);
    const endMatrix = new Matrix4();
    endMatrix.set(
      0.9999954487726056,
      -1.0842021724855044e-19,
      -0.003017024042861,
      0,
      0.00194335285662355,
      0.764916829344874,
      0.6441261270625961,
      0,
      0.0023077724649224894,
      -0.6441290586404133,
      0.7649133480344458,
      0,
      0.00612371289211459,
      -0.9661935879606187,
      1.1473589023161823,
      0.5
    );
    endMatrix.invert();

    function interpolateMatrices(startMatrix, endMatrix, alpha) {
      const interpolatedMatrix = new Matrix4();
      for (let i = 0; i < 16; i++) {
        interpolatedMatrix.elements[i] = MathUtils.lerp(
          startMatrix.elements[i],
          endMatrix.elements[i],
          alpha
        );
      }
      return interpolatedMatrix;
    }

    gsap.to(
      { alpha: 0 },
      {
        duration: 2,
        alpha: 1,
        onUpdate: function () {
          const alpha = this.targets()[0].alpha;
          const interpolatedMatrix = interpolateMatrices(
            startMatrix,
            endMatrix,
            alpha
          );
          this.camera.matrix.copy(interpolatedMatrix);
          this.camera.matrix.decompose(
            this.camera.position,
            this.camera.rotation,
            this.camera.scale
          );
          this.camera.updateProjectionMatrix();
          this.drawFrame();
        }.bind(this),
      }
    );
  }

  initPlayers(positionResults) {
    while (this.playerGroup.children.length > 0) {
      this.playerGroup.remove(this.playerGroup.children[0]);
    }

    positionResults?.map((playerSettings) => {
      const domainWidth = 2 - 0.2; // Domain width
      const domainHeight = 1.2 - 0.1; // Domain height
      const rootPlayerContainer = new Group();
      const container = new Group();

      const textureLoader = new TextureLoader();
      textureLoader.load(
        `https://machinefootballwebapi.azurewebsites.net/PassNetwork/GetPlayerImage?playerId=${playerSettings.playerId}`,
        (texture) => {
          var playerSprite; // Create a canvas
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          // Set canvas size (adjust as needed)
          const size = 256;
          canvas.width = size;
          canvas.height = size;
          // ctx.clearRect(0, 0, size, size);

          // Create a circular clipping path
          ctx.beginPath();
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();

          // Draw white background within the clipped area
          ctx.fillStyle = "rgb(255, 255, 255)";
          ctx.fillRect(0, 0, size, size);

          // Draw the loaded texture onto the canvas
          ctx.drawImage(texture.image, 0, 0, size, size);
          // Draw the loaded texture onto the canvas

          // Create a new texture from the canvas
          const canvasTexture = new CanvasTexture(canvas);
          canvasTexture.colorSpace = SRGBColorSpace;
          // Create sprite material and sprite
          const spriteMaterial = new SpriteMaterial({
            map: canvasTexture,
            transparent: true,
            alphaTest: 0.5,
          });
          playerSprite = new Sprite(spriteMaterial);
          playerSprite.scale.set(0.09, 0.09, 1);
          playerSprite.position.y = 0.04;
          container.add(playerSprite);
        }
      );

      const playerName = new Text();

      playerName.text = playerSettings?.shortName?.toUpperCase();
      playerName.fontSize = 0.018;
      playerName.color = 0xffffff;
      playerName.anchorX = "center";
      playerName.fontWeight = "bold";
      playerName.sync(() => {
        let spacing = 0.020;
        let spacingY = 0.01;
        let size = new Vector3();
        playerName.geometry.boundingBox.getSize(size);
 
        const backgroundGeometry = new PlaneGeometry(
          size.x + spacing,
          size.y + spacingY
        );
        const backgroundMaterial = new MeshBasicMaterial({
          color: 0x313233
        });
        const backgroundMesh = new Mesh(backgroundGeometry, backgroundMaterial);

        backgroundMesh.position.y = -size.y/2 + spacingY/4;
       
        container.add(backgroundMesh);
      });

      container.add(playerName);
      rootPlayerContainer.position.z = 0.1;

      rootPlayerContainer.position.x =
        (playerSettings.xCoordinate / this.fieldDomain.u) * domainWidth -
        domainWidth / 2;
      rootPlayerContainer.position.x += 0.07;
      rootPlayerContainer.position.y =
        domainHeight / 2 -
        (playerSettings.yCoordinate / this.fieldDomain.v) * domainHeight;
      rootPlayerContainer.position.y -= 0.06;

      rootPlayerContainer.add(container);
      container.position.y = 0.02;

      this.playerContainers.push(rootPlayerContainer);
      this.playerGroup.add(rootPlayerContainer);
    });
  }
}

export { Pitch };
